(() => {
  'use strict';

  if (window.PendantCheckoutRecalculation) return;

  // Paid pendant variants and free pendant variants are totaled by category.
  const PAID_VARIANT_IDS = new Set(['67658885267610', '67658885300378']);
  const FREE_VARIANT_IDS = new Set(['67658833952922', '67658833985690']);
  const DEFAULT_FREE_VARIANT_ID = '67658833952922';
  const CHECKOUT_FORM_IDS = new Set(['CartDrawer-Form', 'cart', 'cart-notification-form']);
  const ADJUSTMENT_MESSAGE = 'Free pendant quantity adjusted to match the offer limit.';
  const FIVE_FREE_MESSAGE = 'Free pendant quantity adjusted: maximum 5 free pendants per order.';
  const CART_REFRESH_FLAG = 'pendant-cart-adjusted';

  let processing = false;
  const bypassForms = new WeakSet();

  const delay = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  function removeAdjustmentNotice() {
    document.getElementById('PendantCheckoutAdjustmentNotice')?.remove();
  }

  function showAdjustmentNotice(message) {
    removeAdjustmentNotice();

    const notice = document.createElement('div');
    notice.id = 'PendantCheckoutAdjustmentNotice';
    notice.setAttribute('role', 'status');
    notice.setAttribute('aria-live', 'polite');
    notice.setAttribute('aria-atomic', 'true');
    notice.textContent = message;
    notice.style.cssText = [
      'position:fixed',
      'left:50%',
      'top:50%',
      'transform:translate(-50%,-50%)',
      'z-index:2147483647',
      'width:max-content',
      'max-width:calc(100vw - 32px)',
      'padding:18px 24px',
      'box-sizing:border-box',
      'border-radius:10px',
      'background:rgba(20,20,20,.94)',
      'color:#fff',
      'box-shadow:0 4px 16px rgba(0,0,0,.2)',
      'font:inherit',
      'font-size:18px',
      'line-height:1.4',
      'text-align:center',
      'pointer-events:none',
    ].join(';');
    document.body.appendChild(notice);
  }

  // Mobile browsers can restore the cart page from the back-forward cache.
  window.addEventListener('pagehide', removeAdjustmentNotice);
  window.addEventListener('pageshow', (event) => {
    removeAdjustmentNotice();
    if (event.persisted && window.sessionStorage.getItem(CART_REFRESH_FLAG) === 'true') {
      window.sessionStorage.removeItem(CART_REFRESH_FLAG);
      window.location.reload();
    }
  });

  function jsonEndpoint(url) {
    return url.endsWith('.js') ? url : `${url}.js`;
  }

  function cartUrl() {
    return jsonEndpoint(window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`);
  }

  function updateUrl() {
    return jsonEndpoint(window.routes?.cart_update_url || `${window.Shopify?.routes?.root || '/'}cart/update`);
  }

  function addUrl() {
    return jsonEndpoint(window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add`);
  }

  function checkoutUrl() {
    return `${window.Shopify?.routes?.root || '/'}checkout`;
  }

  async function getCart() {
    const response = await fetch(cartUrl(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
    return response.json();
  }

  // Deterministic largest-remainder allocation preserves the original proportions.
  function apportion(entries, targetTotal, quantityOf, orderOf, allowIncrease = false) {
    const originalTotal = entries.reduce((sum, entry) => sum + quantityOf(entry), 0);
    if (!originalTotal || !targetTotal) return new Map(entries.map((entry) => [entry, 0]));

    const allocations = entries.map((entry) => {
      const original = quantityOf(entry);
      const raw = (original / originalTotal) * targetTotal;
      const quantity = allowIncrease ? Math.floor(raw) : Math.min(original, Math.floor(raw));
      return { entry, original, quantity, remainder: raw - quantity, order: orderOf(entry) };
    });

    let remaining = targetTotal - allocations.reduce((sum, item) => sum + item.quantity, 0);
    const ranked = [...allocations].sort(
      (left, right) => right.remainder - left.remainder || left.order - right.order,
    );

    while (remaining > 0) {
      let distributed = false;
      for (const item of ranked) {
        if (!remaining) break;
        if (!allowIncrease && item.quantity >= item.original) continue;
        item.quantity += 1;
        remaining -= 1;
        distributed = true;
      }
      if (!distributed) throw new Error('Unable to apportion free pendant quantities');
    }

    return new Map(allocations.map((item) => [item.entry, item.quantity]));
  }

  function correctionFor(cart) {
    const lines = Array.isArray(cart?.items) ? cart.items : [];
    let totalPaid = 0;
    const freeLines = [];

    lines.forEach((line, index) => {
      const variantId = String(line.variant_id ?? line.id ?? '');
      const quantity = Math.max(0, Number(line.quantity) || 0);
      if (PAID_VARIANT_IDS.has(variantId)) totalPaid += quantity;
      if (FREE_VARIANT_IDS.has(variantId)) {
        freeLines.push({ key: line.key, variantId, quantity, index });
      }
    });

    const totalFree = freeLines.reduce((sum, line) => sum + line.quantity, 0);
    if (!totalPaid && !totalFree) return null;

    // Without paid pendants: reduce only above 5. With paid pendants: enforce exactly 2 free per paid.
    const maximumFree = totalPaid > 0 ? totalPaid * 2 : 5;
    if (totalPaid === 0 && totalFree <= maximumFree) return null;
    if (totalPaid > 0 && totalFree === maximumFree) return null;

    if (totalPaid > 0 && totalFree === 0) {
      return {
        additions: [{ id: DEFAULT_FREE_VARIANT_ID, quantity: maximumFree }],
        updates: null,
        targetFreeQuantity: maximumFree,
        direction: 'increase',
      };
    }

    const groupsByVariant = new Map();
    freeLines.forEach((line) => {
      let group = groupsByVariant.get(line.variantId);
      if (!group) {
        group = { variantId: line.variantId, quantity: 0, firstIndex: line.index, lines: [] };
        groupsByVariant.set(line.variantId, group);
      }
      group.quantity += line.quantity;
      group.lines.push(line);
    });

    const groups = [...groupsByVariant.values()];
    const increasing = totalPaid > 0 && totalFree < maximumFree;
    const groupTargets = apportion(
      groups,
      maximumFree,
      (group) => group.quantity,
      (group) => group.firstIndex,
      increasing,
    );

    // If proportional rounding would erase either free variant, retain one
    // unit of every originally represented variant when the allowance permits.
    if (!increasing && maximumFree >= groups.length) {
      const zeroTargetGroups = groups
        .filter((group) => groupTargets.get(group) === 0)
        .sort((left, right) => left.firstIndex - right.firstIndex);

      zeroTargetGroups.forEach((zeroTargetGroup) => {
        const donor = groups
          .filter((group) => group !== zeroTargetGroup && groupTargets.get(group) > 1)
          .sort(
            (left, right) =>
              groupTargets.get(right) - groupTargets.get(left) ||
              right.quantity - left.quantity ||
              left.firstIndex - right.firstIndex,
          )[0];

        if (donor) {
          groupTargets.set(zeroTargetGroup, 1);
          groupTargets.set(donor, groupTargets.get(donor) - 1);
        }
      });
    }

    const updates = {};

    groups.forEach((group) => {
      const lineTargets = apportion(
        group.lines,
        groupTargets.get(group),
        (line) => line.quantity,
        (line) => line.index,
        increasing,
      );
      group.lines.forEach((line) => {
        const target = lineTargets.get(line);
        if (target !== line.quantity) {
          if (!line.key) throw new Error('Free pendant line key is unavailable');
          updates[line.key] = target;
        }
      });
    });

    return Object.keys(updates).length
      ? {
          additions: null,
          updates,
          targetFreeQuantity: maximumFree,
          direction: increasing ? 'increase' : 'reduce',
        }
      : null;
  }

  async function applyCorrection(updates) {
    const response = await fetch(updateUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    });
    if (!response.ok) throw new Error(`Cart update failed (${response.status})`);
    return response.json();
  }

  async function applyAdditions(additions) {
    const response = await fetch(addUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: additions }),
    });
    if (!response.ok) throw new Error(`Cart add failed (${response.status})`);
    await response.json();
    return getCart();
  }

  function totalFreeQuantity(cart) {
    return (Array.isArray(cart?.items) ? cart.items : []).reduce((total, line) => {
      const variantId = String(line.variant_id ?? line.id ?? '');
      return FREE_VARIANT_IDS.has(variantId) ? total + (Number(line.quantity) || 0) : total;
    }, 0);
  }

  function hasPaidPendant(cart) {
    return (Array.isArray(cart?.items) ? cart.items : []).some((line) => {
      const variantId = String(line.variant_id ?? line.id ?? '');
      return PAID_VARIANT_IDS.has(variantId) && (Number(line.quantity) || 0) > 0;
    });
  }

  async function refreshThemeCart(updatedCart) {
    if (typeof window.publish !== 'function' || !window.PUB_SUB_EVENTS?.cartUpdate) return;
    try {
      await Promise.race([
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'pendant-checkout-recalculation',
          cartData: updatedCart,
        }),
        delay(1500),
      ]);
    } catch (error) {
      console.warn('Adjusted cart UI refresh was skipped:', error);
    }
  }

  function isCheckoutSubmitter(event) {
    const submitter = event.submitter || document.activeElement;
    return submitter instanceof HTMLElement && submitter.getAttribute('name') === 'checkout' ? submitter : null;
  }

  function continueCheckout(form, submitter) {
    processing = false;
    if (!form?.isConnected) {
      window.location.assign(checkoutUrl());
      return;
    }

    bypassForms.add(form);
    if (typeof form.requestSubmit === 'function' && submitter?.isConnected) form.requestSubmit(submitter);
    else window.location.assign(checkoutUrl());
  }

  async function recalculateAndContinue(form, submitter) {
    try {
      const cart = await getCart();
      const adjustment = correctionFor(cart);
      if (!adjustment) {
        continueCheckout(form, submitter);
        return;
      }

      const updatedCart = adjustment.additions
        ? await applyAdditions(adjustment.additions)
        : await applyCorrection(adjustment.updates);
      if (totalFreeQuantity(updatedCart) !== adjustment.targetFreeQuantity) {
        throw new Error('Shopify did not confirm the corrected free pendant quantity');
      }

      window.sessionStorage.setItem(CART_REFRESH_FLAG, 'true');

      // Do not resubmit stale cart quantity inputs after a correction; enter checkout directly.
      if (adjustment.direction === 'reduce') {
        await refreshThemeCart(updatedCart);
        showAdjustmentNotice(hasPaidPendant(cart) ? ADJUSTMENT_MESSAGE : FIVE_FREE_MESSAGE);
        await delay(3000);
        removeAdjustmentNotice();
      }
      processing = false;
      window.location.assign(checkoutUrl());
    } catch (error) {
      // Recalculation is silent and fail-open: never trap the customer outside checkout.
      console.warn('Pendant checkout recalculation was skipped:', error);
      continueCheckout(form, submitter);
    }
  }

  function startRecalculation(event, form, submitter) {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (processing) return;
    processing = true;
    void recalculateAndContinue(form, submitter);
  }

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      const submitter = target?.closest?.('button[name="checkout"], input[name="checkout"]');
      const form = submitter?.form;
      if (!(form instanceof HTMLFormElement) || !CHECKOUT_FORM_IDS.has(form.id)) return;
      startRecalculation(event, form, submitter);
    },
    true,
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !CHECKOUT_FORM_IDS.has(form.id)) return;

      if (bypassForms.has(form)) {
        bypassForms.delete(form);
        return;
      }

      const submitter = isCheckoutSubmitter(event);
      if (!submitter) return;
      startRecalculation(event, form, submitter);
    },
    true,
  );

  window.PendantCheckoutRecalculation = true;
})();
